/**
 * Constants for Customer Confidence Call - Consolidated Constants
 * These can be easily modified for localization or to add/remove options
 */
/**
 * Call Type options for Customer Confidence Call - Step 1
 */
export const CALL_TYPE_OPTIONS = [
    {
        key: "outage",
        label: "Outage",
        description: "Follow-up after an Azure outage (typically post-PIR).",
        iconName: "Warning",
        iconColor: "#f7630c",
        requiresIncident: true,
        requiresSupportRequest: false,
        isEnabled: true,
    },
    {
        key: "product",
        label: "Product",
        description: "An issue or ongoing issue with a specific Azure product.",
        iconName: "Product",
        iconColor: "#a5673f",
        requiresIncident: false,
        requiresSupportRequest: true,
        incidentOptionalNote: "*",
        isEnabled: true,
    },
    {
        key: "reliability",
        label: "Reliability",
        description: "Platform reliability discussions, e.g. recurring outages.",
        iconName: "Shield",
        iconColor: "#2684ff",
        requiresIncident: true,
        requiresSupportRequest: false,
        isEnabled: true,
    },
    {
        key: "quality",
        label: "Quality",
        description: "Azure quality roadmap and quality-pillar topics.",
        iconName: "TestBeaker",
        iconColor: "#4caf50",
        requiresIncident: false,
        requiresSupportRequest: false,
        isEnabled: true,
    },
    {
        key: "capacity",
        label: "Capacity",
        description: "Capacity topics excluding quota denials.",
        iconName: "BarChartVertical",
        iconColor: "#8764b8",
        requiresIncident: false,
        requiresSupportRequest: false,
        isEnabled: true,
    },
];
/**
 * Maps a selected call type key to the backend submissionType token.
 * All five Azure scenarios share an identical flow; only the submissionType differs.
 */
export const CALL_TYPE_TO_SUBMISSION_TYPE = {
    outage: "azure-outage-confidence-call",
    product: "azure-product-confidence-call",
    quality: "azure-quality-confidence-call",
    reliability: "azure-reliability-confidence-call",
    capacity: "azure-capacity-technical-nonquota",
};
/**
 * Default submissionType used when a call type does not have an explicit mapping.
 */
export const DEFAULT_SUBMISSION_TYPE = "azure-outage-confidence-call";
/**
 * Segment submitted to RM2 for this commercial confidence-call journey.
 * RM2 matches customerSegmentName and accountSegment exactly against its own
 * segment list, whose only active values are Commercial, Consumer and Partner.
 * MS Sales segments such as "Strategic Commercial" are not valid, and RM2 has
 * no "Enterprise" segment. Partner and Consumer submissions are out of scope
 * for this journey because they carry different identifier rules.
 */
export const RM2_COMMERCIAL_SEGMENT = "Commercial";
/**
 * Call type keys that drive the Azure incident/outage + support ticket flow.
 * These all present the same form; only the submissionType differs.
 */
export const AZURE_FLOW_CALL_TYPES = new Set([
    ...CALL_TYPE_OPTIONS.filter((option) => option.isEnabled && !option.isHidden).map((option) => option.key),
]);
/**
 * Call type keys that require at least one linked incident/outage (ICM) to be
 * selected before the escalation can be submitted.
 *
 * The UI requirement is defined on each call type above so the tile badges and
 * form gating cannot drift apart.
 */
export const CALL_TYPES_REQUIRING_INCIDENT = new Set([
    ...CALL_TYPE_OPTIONS.filter((option) => option.requiresIncident).map((option) => option.key),
]);
export const CALL_TYPES_REQUIRING_SUPPORT_REQUEST = new Set([
    ...CALL_TYPE_OPTIONS.filter((option) => option.requiresSupportRequest).map((option) => option.key),
]);
/**
 * Returns true when the given call type requires a linked incident/outage (ICM)
 * before submission. Known Azure call types are governed by
 * CALL_TYPES_REQUIRING_INCIDENT; unknown/empty call types default to requiring
 * an incident (fail-safe) so a new or malformed type can never silently bypass
 * the backend-mandated incident requirement.
 */
export const callTypeRequiresIncident = (callType) => !AZURE_FLOW_CALL_TYPES.has(callType) ||
    CALL_TYPES_REQUIRING_INCIDENT.has(callType);
export const callTypeRequiresSupportRequest = (callType) => CALL_TYPES_REQUIRING_SUPPORT_REQUEST.has(callType);
/**
 * Technical Depth options for Customer Confidence Call
 */
export const TECHNICAL_DEPTH_OPTIONS = [
    {
        key: "executiveBusiness",
        label: "Executive/Business (strategic, non-technical)",
        iconName: "People",
        iconColor: "#5c2d91",
        iconBackgroundColor: "#f4ecfb",
    },
    {
        key: "mixed",
        label: "Mixed (executive and technical)",
        iconName: "Teamwork",
        iconColor: "#006666",
        iconBackgroundColor: "#e7f5f5",
    },
    {
        key: "technical",
        label: "Technical (deep dive, engineering-level)",
        iconName: "DeveloperTools",
        iconColor: "#005a9e",
        iconBackgroundColor: "#deecf9",
    },
];
/**
 * Escalation reason options for Context & Rationale
export const ESCALATION_REASON_OPTIONS: EscalationReasonOption[] = [
  { key: "customerConfidenceConcerns", label: "Customer confidence concerns" },
  { key: "serviceReliabilityIssues", label: "Service reliability issues" },
  { key: "outageIncidentFollowUp", label: "Outage or incident follow-up" },
  {
    key: "productBehaviorQualityConcerns",
    label: "Product behaviour or quality concerns",
  },
  { key: "changeManagementImpact", label: "Change management impact" },
  { key: "communicationGaps", label: "Communication gaps" },
  { key: "competeRenewalBlocker", label: "Compete or renewal blocker" },
  { key: "securityComplianceRisk", label: "Security or compliance risk" },
  {
    key: "escalationFromCSAMAccountTeam",
    label: "Escalation from CSAM or Account Team",
  },
  {
    key: "trackingOnlyCallOrganisedOutside",
    label: "Tracking only – call organised outside GetHelp",
  },
];
*/
export const ESCALATION_REASON_OPTIONS = [
    {
        key: "bypassProcessJustified",
        label: "Bypass Process - Justified",
    },
    {
        key: "supportCaseResolutionRequiredAnswerUnacceptable",
        label: "Support Case Resolution Required/Answer Unacceptable",
    },
    {
        key: "existingProcessChannelNotEffective",
        label: "Existing Process/Channel Not Effective",
    },
    {
        key: "highRiskCaseIncreaseVisibility",
        label: "High Risk Case - Increase Visibility",
    },
    {
        key: "getHelpCaseRequired",
        label: "GetHelp Case Required",
    },
    {
        key: "noCurrentEscalationPath",
        label: "No Current Escalation Path",
    },
    {
        key: "priorResponseAnswerUnacceptable",
        label: "Prior Response/Answer is Unacceptable",
    },
    {
        key: "supportOrFieldUnawareOfDocumentation",
        label: "Support or Field Unaware of Existing Documentation/Information",
    },
    {
        key: "cpaManagesTheProcess",
        label: "CPA Manages the Process",
    },
    {
        key: "bypassProcess",
        label: "Bypass Process",
    },
    {
        key: "missedSupportSla",
        label: "Missed Support SLA",
    },
    {
        key: "poorSupportExperience",
        label: "Poor Support Experience",
    },
    {
        key: "noSupportContract",
        label: "No Support Contract",
    },
    {
        key: "poorSupportQuality",
        label: "Poor Support Quality",
    },
    {
        key: "accountTransition",
        label: "Account Transition",
    },
    {
        key: "skypeForBusinessToTeamsMigration",
        label: "Skype for Business to Teams Migration",
    },
    {
        key: "externalUnknownDoNotUse",
        label: "External – Unknown (Do Not Use)",
    },
    {
        key: "usingGetHelpSubmissionPage",
        label: "Using GetHelp Submission Page",
    },
    {
        key: "engineeringResolutionRequiredAnswerUnacceptable",
        label: "Engineering Resolution Required/Answer Unacceptable",
    },
    {
        key: "supportDeliveryIssue",
        label: "Support Delivery Issue",
    },
    {
        key: "noEscalationPathExists",
        label: "No Escalation Path Exists",
    },
    {
        key: "other",
        label: "Other",
    },
    {
        key: "supportFieldProcessIssue",
        label: "Support/Field Process Issue",
    },
    {
        key: "customerDoesNotAcceptEngineeringResolution",
        label: "Customer Does Not Accept Engineering Resolution",
    },
    {
        key: "additionalHandlingRequiredByDesign",
        label: "Additional Handling Required by Design",
    },
    {
        key: "supportFieldDeliveryIssue",
        label: "Support/Field Delivery Issue",
    },
    {
        key: "supportFieldCommunicationIssue",
        label: "Support/Field Communication Issue",
    },
    {
        key: "customerDoesNotAcceptSupportResolution",
        label: "Customer Does Not Accept Support Resolution",
    },
    {
        key: "customerExpectationsOrActions",
        label: "Customer Expectations or Actions",
    },
    {
        key: "partnerExpectationsOrActions",
        label: "Partner Expectation or Actions",
    },
    {
        key: "support",
        label: "Support",
    },
    {
        key: "engineering",
        label: "Engineering",
    },
    {
        key: "customer",
        label: "Customer",
    },
    {
        key: "partner",
        label: "Partner",
    },
    {
        key: "field",
        label: "Field",
    },
    {
        key: "microsoft",
        label: "Microsoft",
    },
];
/**
 * Azure Workload options for Customer Confidence Call
 */
export const WORKLOAD_OPTIONS = [
    { key: "virtualMachines", label: "Virtual Machines (VMs)" },
    { key: "appServices", label: "App Services / Web Apps" },
    { key: "azureSql", label: "Azure SQL / Databases" },
    { key: "storageAccounts", label: "Storage Accounts" },
    { key: "networking", label: "Networking (VNET, VPN, ExpressRoute)" },
    { key: "containers", label: "Containers / AKS" },
    {
        key: "dataAnalytics",
        label: "Data & Analytics (Synapse, Data Factory, Databricks)",
    },
    { key: "aiMachineLearning", label: "AI / Machine Learning" },
    {
        key: "securityIdentity",
        label: "Security / Identity (Azure AD, Key Vault)",
    },
];
/**
 * Azure Component options for Customer Confidence Call
 */
export const COMPONENT_OPTIONS = [
    { key: "azureSqlDatabase", label: "Azure SQL Database" },
    { key: "blobStorage", label: "Blob Storage" },
    { key: "virtualMachineScaleSet", label: "Virtual Machine Scale Set" },
    { key: "aks", label: "AKS (Kubernetes)" },
];
/**
 * Labels and validation messages - Consolidated
 */
export const LABELS = {
    // Call Type
    callTypeQuestion: "Select the Azure escalation type:",
    callTypeValidation: "Please select a call type to continue.",
    // Technical Depth
    technicalDepthQuestion: "Select expected technical depth:",
    technicalDepthValidation: "Please select a technical depth level.",
    // Context & Rationale
    reasonQuestion: "Why is this being escalated?",
    desiredOutcomeLabel: "What are the desired results/actions for this call?",
    desiredOutcomePlaceholder: "Describe the expected outcome of this confidence call",
    businessRiskLabel: "Business Impact",
    businessRiskPlaceholder: "Describe the business impact",
    additionalStakeholdersLabel: "Additional Stakeholders",
    additionalStakeholdersPlaceholder: "Search and select additional stakeholders",
    attachmentsLabel: "Attachments",
    attachmentsPlaceholder: "Add any relevant attachments",
    validationMessageReasons: "Please select at least one escalation reason.",
    validationMessageDesiredOutcome: "Desired outcome is required.",
    // Product Component & Workloads
    componentsQuestion: "Which Azure components or services are impacted? Select all that apply:",
    workloadOtherPlaceholder: "Please specify other workload",
    componentOtherPlaceholder: "Please specify other component",
    productValidationMessage: "Please select at least one workload and one component. If 'Other' is selected, please provide a description.",
};
